import { useState } from 'react';
import { submitInvestorApplication, type InvestorRole } from '../../services/investorsService';

interface ApplyFormProps {
  isRTL: boolean;
  defaultRole?: InvestorRole;
  developerSlug?: string;
}

export default function ApplyForm({ isRTL, defaultRole = 'INVESTOR', developerSlug }: ApplyFormProps) {
  const [role, setRole] = useState<InvestorRole>(defaultRole);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [projectOrBudget, setProjectOrBudget] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const t = (en: string, ar: string) => (isRTL ? ar : en);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setErrorMsg(t('Please enter your name and email.', 'يرجى إدخال الاسم والبريد الإلكتروني.'));
      setStatus('error');
      return;
    }
    setStatus('submitting');
    setErrorMsg('');
    try {
      await submitInvestorApplication({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        role,
        projectOrBudget: projectOrBudget.trim() || undefined,
        message: message.trim() || undefined,
        developerSlug,
      });
      setStatus('done');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : t('Something went wrong.', 'حدث خطأ ما.'));
      setStatus('error');
    }
  };

  if (status === 'done') {
    return (
      <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-8 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
          <i className="ri-check-line text-2xl text-emerald-600" />
        </div>
        <h3 className="text-lg font-bold text-emerald-900">{t('Thank you - we received your request', 'شكراً لك - تم استلام طلبك')}</h3>
        <p className="mt-2 text-sm text-emerald-700">
          {t('Our team will reach out to you shortly.', 'سيتواصل معك فريقنا قريباً.')}
        </p>
      </div>
    );
  }

  const inputCls = 'w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition-colors focus:border-[#004D40] focus:ring-2 focus:ring-[#DDEEEB]';

  return (
    <form onSubmit={onSubmit} className={`rounded-3xl border border-slate-200/80 bg-white p-6 sm:p-8 shadow-sm ${isRTL ? 'text-right' : ''}`}>
      {/* Role toggle */}
      <div className="mb-5 inline-flex rounded-full bg-slate-100 p-1">
        {(['DEVELOPER', 'INVESTOR'] as const).map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRole(r)}
            className={`rounded-full px-5 py-2 text-sm font-medium transition-all ${
              role === r ? 'bg-white text-[#003930] shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {r === 'DEVELOPER' ? t('I am a developer', 'أنا مطوّر') : t('I am an investor', 'أنا مستثمر')}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">{t('Full name', 'الاسم الكامل')} *</label>
          <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">{t('Email', 'البريد الإلكتروني')} *</label>
          <input type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} required dir="ltr" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">{t('Phone', 'الهاتف')}</label>
          <input className={inputCls} value={phone} onChange={(e) => setPhone(e.target.value)} dir="ltr" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">
            {role === 'DEVELOPER' ? t('Project / scope', 'المشروع / النطاق') : t('Investment budget', 'ميزانية الاستثمار')}
          </label>
          <input className={inputCls} value={projectOrBudget} onChange={(e) => setProjectOrBudget(e.target.value)} />
        </div>
      </div>

      <div className="mt-4">
        <label className="mb-1 block text-xs font-medium text-slate-500">{t('Message', 'رسالتك')}</label>
        <textarea className={`${inputCls} min-h-[96px] resize-y`} value={message} onChange={(e) => setMessage(e.target.value)} />
      </div>

      {status === 'error' && <p className="mt-3 text-sm text-rose-600">{errorMsg}</p>}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="mt-6 w-full rounded-full bg-[#004D40] px-6 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
      >
        {status === 'submitting' ? t('Submitting…', 'جارٍ الإرسال…') : t('Submit request', 'إرسال الطلب')}
      </button>
    </form>
  );
}
